import { useTranslation } from '@conekta/cronos/i18n';
import Alert from 'app/components/Alert/Alert';
import './styles.scss';
import { CheckoutFrameContext } from 'app/context/CheckoutFrameContext';
import { getXprops } from 'app/helpers/getXprops';
import { AgreementContentPrivacyPolicy, PaymentMethodContent, FormShipping } from 'app/pages/frames/components';
import { redirectToHostedPaymentUrl } from 'app/pages/frames/helpers/redirections';
import { getErrorMessageForCustomer } from 'app/pages/frames/util/form';
import { sendAnalytic, sendFormAnalytics } from 'app/util/analytics';
import {
  AnalyticEvents,
  checkoutRequestType,
  PaymentMethodType,
  ThreeDsValues,
  truncateButtonStringLength,
} from 'app/util/constants';
import { truncateString } from 'app/util/strings';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { ThemeContext } from 'styled-components';

import { BasicFormCard } from './BasicFormCard';
import { FormMethodCard, WrapperFormCard } from './styles';

const tRoot: string = 'iFrame.checkout';

interface Props {
  handleSubmit: (values?: any) => void;
  appContext?: any;
  error?: any;
  loading?: boolean;
  data?: any;
  showShippingForm?: boolean;
  initialValues: Record<string, any>;
  onFinalizePayment?: () => void;
  threeDsFlag?: ThreeDsValues;
}

export const FormCard = ({
  onFinalizePayment,
  handleSubmit: onSubmit,
  initialValues,
  showShippingForm,
  error,
  loading,
  data,
  appContext,
}: Props) => {
  const { t } = useTranslation();
  const themeContext = useContext(ThemeContext);
  const { checkoutRequest, isIntegration } = useContext(CheckoutFrameContext);
  const [errorMessage, setErrorMessage] = useState<any>();
  const [formValues, setFormValues] = useState<any>(undefined);
  const {
    allowTokenization,
    integration: isIntegrationRequested,
    callbacks: { onFinalizePayment: extOnFinalizePayment, onErrorPayment },
    options,
  } = getXprops();
  const { font } = themeContext.colors;
  const { id, companyId, type, redirectionTime, needsShippingContact } = checkoutRequest;
  const submitButtonFromXprops: string = options?.button?.text ?? null;

  const submitButtonText: string = submitButtonFromXprops
    ? truncateString(submitButtonFromXprops, truncateButtonStringLength)
    : type === checkoutRequestType.integration
    ? t(`${tRoot}.buttonSubmitMakeAndOrder`)
    : t(`${tRoot}.buttonSubmitFinalize`);

  useEffect(() => {
    if (error) {
      const errorMessageCustom = getErrorMessageForCustomer(error);
      const errorMessageValue = errorMessageCustom ? errorMessageCustom : t(`iFrame.checkout.cardForm.errorMisc`);
      onErrorPayment?.(errorMessageValue);
      setErrorMessage(errorMessageValue);
      if (type === checkoutRequestType.hostedPayment && isIntegration) {
        redirectToHostedPaymentUrl(error?.urlRedirect, redirectionTime);
      }
    }
  }, [error]);

  useEffect(() => {
    if (data?.order) {
      const { order } = data;
      extOnFinalizePayment && extOnFinalizePayment(order);
      onFinalizePayment?.();
      appContext?.setConfirmation({
        ...(appContext?.confirmation || {}),
        orderResponse: data,
        selectedPaymentMethod: PaymentMethodType.Card,
        shippingContact: data.confirmationShippingContact,
      });
    }
  }, [data]);

  const onCardFormSubmit = useCallback((values: BasicFormCardValues) => {
    appContext?.setExtraData?.((prev: any) => ({ ...prev, formCardData: values }));
    onLocalSubmit(values);
  }, []);

  const onLocalSubmit = (values: any) => {
    const newValues = { ...formValues, ...values };
    onSubmit(newValues);
    sendFormAnalytics(newValues, id, companyId, type);
    sendAnalytic(AnalyticEvents.paymentButton, id, companyId, type);
    setFormValues(newValues);
  };

  return (
    <Form
      onSubmit={onCardFormSubmit}
      initialValues={initialValues}
      render={({ form, handleSubmit, submitting, values }) => (
        <WrapperFormCard
          className="FormCard"
          onSubmit={handleSubmit}
          disabledBorder={!!allowTokenization}
          iframeBackgroundColor={options?.iframe?.backgroundColor}
          iframeColorText={options?.iframe?.colorText}
          buttonBackgroundColor={options?.button?.backgroundColor}
          buttonColorText={options?.button?.colorText}
          noValidate
          {...(showShippingForm ? { as: 'div' } : {})}
        >
          <PaymentMethodContent>
            <FormMethodCard color={font} className="FormCard__form">
              {errorMessage && (
                <Alert type="error" className="FormCard__alert">
                  {errorMessage}
                </Alert>
              )}
              {showShippingForm ? (
                <FormShipping
                  submitButtonText={submitButtonText}
                  isLoading={!!loading}
                  handleFormSubmit={onLocalSubmit}
                />
              ) : (
                <BasicFormCard
                  loading={loading}
                  form={form}
                  submitting={submitting}
                  values={values}
                  error={error}
                  setErrorMessage={setErrorMessage}
                  submitButtonText={needsShippingContact ? t(`${tRoot}.cardForm.buttonNext`) : submitButtonText}
                />
              )}
            </FormMethodCard>

            {!isIntegrationRequested && <AgreementContentPrivacyPolicy />}
          </PaymentMethodContent>
        </WrapperFormCard>
      )}
    />
  );
};

interface BasicFormCardValues {
  cardExpMonthYear: string;
  cardNumber: string;
  cardVerificationValue: string;
  cardholderName: string;
  customerInfo: {
    email: string;
    name: string;
  };
  monthlyInstallments: number;
  savePaymentSource: boolean;
}
